import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useLocation, useNavigate } from "react-router-dom";

// Import the enhanced tour provider
import { useTour } from './TourProvider'; // Adjust path as needed

import LevelSelectionCarousel from "./LevelSelectionCarousel";
import OpponentSelectionCarousel from "./OpponentSelectionCarousel";
import useOpponentsList from "../entities/useOpponentsList.js";
import useLevelsList from "../entities/useLevelsList.js";
import MovingText from "react-moving-text";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { LottieAnimation } from "../lottie/LottieAnimation.js";
import VideoPlayer from "./VideoPlayer";
import GameInfoDialog from "./GameInfoDialog";
import { useWebSocket } from "../services/WebSocketProvider";
import NotEnoughCoinsDialog from "./NotEnoughCoinsDialog";

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { GENERAL } from "../general/General";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import CancelIcon from '@mui/icons-material/Cancel';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TourIcon from '@mui/icons-material/Tour';
import GroupIcon from '@mui/icons-material/Group';
import { useMainPageTour } from './useMainPageTour'; // Import the external hook
// New imports for enhanced level card
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Card,
  CardContent,
  Chip,
  IconButton,
  Fab,
  Slide,
  Zoom,
  LinearProgress,
  Avatar,
  Backdrop,
  Badge,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  SportsEsports,
  Star,
  EmojiEvents,
  Speed,
  Balance as BalanceIcon,
  Close as CloseIcon,
  Close,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const MainPage = ({ userData, updateBackground, setShowAppBar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { state } = location;
  const { generalMessage } = state || {};
  const { t } = useTranslation();
  const opponents = useOpponentsList();
  const levels = useLevelsList();
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [openDialog, setOpenDialog] = useState(false);
  const [uncompletedGame, setUncompletedGame] = useState(null);
  const { uncompletedGameWS } = useWebSocket();
  const [gameInfoDialogOpen, setGameInfoDialogOpen] = useState(false);
  const [showOpponentSelection, setShowOpponentSelection] = useState(false);
  const [showLevelSelection, setShowLevelSelection] = useState(false);
  
  // Enhanced tour hook usage - pass translation function
  const { 
    startMainPageTour, 
    forceShowMainPageTour, 
    shouldShowTour, 
    resetTour,
    hasAutoStarted 
  } = useMainPageTour(t); // Pass the translation function
  
  const [buttonSelectOpponent, setButtonSelectOpponent] = useState(t("Select_opponent"));
  const [buttonSelectLevel, setButtonSelectLevel] = useState(t("Level_Select"));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [neededCoins, setNeededCoins] = useState(null);
  const [actualCoins, setActualCoins] = useState(null);
  const [selectedOpponent, setSelectedOpponent] = useState("");
  const [autoTourTriggered, setAutoTourTriggered] = useState(false);
  const [, setTimeLimitText] = useState("");

  // New state for enhanced UI
  const [showGameStats, setShowGameStats] = useState(false);
  const [animatePlay, setAnimatePlay] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(true);

  // Helper function to get current level data
  const getCurrentLevelData = useCallback(() => {
    const levelId = localStorage.getItem("lastSelectedLevelId");
    const storedLevels = JSON.parse(localStorage.getItem("levels"));
    return storedLevels?.find((level) => level.levelTypeId === +levelId) || null;
  }, []);

  // Outcome Item Component for level card - Mobile optimized
  const OutcomeItem = ({ icon, coins, color }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        p: 1,
        borderRadius: 2,
        background: `linear-gradient(45deg, ${color}20, ${color}10)`,
        border: `1px solid ${color}40`,
        minWidth: 0,
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: color,
          color: 'white',
          fontSize: 16,
        }}
      >
        {icon}
      </Box>
      {coins !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Typography
            variant="caption"
            sx={{
              color: coins >= 0 ? '#4CAF50' : '#f44336',
              fontWeight: 'bold',
              fontSize: '0.9rem',
            }}
          >
            {coins}
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Enhanced tour conditions - fix shouldShowTour usage
  const tourConditions = useMemo(() => ({
    canShowTour: !loading && !showOpponentSelection && !showLevelSelection,
    shouldShow: typeof shouldShowTour === 'function' ? shouldShowTour() : false,
    hasTriggered: autoTourTriggered || hasAutoStarted
  }), [loading, showOpponentSelection, showLevelSelection, shouldShowTour, autoTourTriggered, hasAutoStarted]);

  // Utility function for setting button labels from local storage
  const setButtonLabelsFromLocalStorage = useCallback(() => {
    const lastSelectedIndexLevel = localStorage.getItem("lastSelectedIndexLevel");
    const lastSelectedOpponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    const lastTimeLimit = localStorage.getItem("lastTimeLimit");

    if (lastSelectedIndexLevel && levels && levels.length > 0) {
      setButtonSelectLevel(levels[lastSelectedIndexLevel].name);
    }

    if (lastSelectedOpponentIndex && opponents && opponents.length > 0) {
      setButtonSelectOpponent(opponents[lastSelectedOpponentIndex]?.name);
    }

    if (lastTimeLimit === "1") {
      setTimeLimitText(t("MINUTES_PER_MOVE_1"));
    } else if (lastTimeLimit === "30") {
      setTimeLimitText(t("MINUTES_PER_GAME_30"));
    } else if (lastTimeLimit === "5") {
      setTimeLimitText(t("МINUTES_PER_GAME_5"));
    } else if (lastTimeLimit === "3") {
      setTimeLimitText(t("MINUTES_PER_GAME_3"));
    } else {
      setTimeLimitText(t(lastTimeLimit));
    }
  }, [levels, opponents, t]);

  // Static Time Limit Display Component - NO ANIMATIONS
  const TimeLimitBadge = () => {
    const getTimeLimitIcon = () => {
      const lastTimeLimit = localStorage.getItem("lastTimeLimit");
      const lastSelectedOpponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
      
      if(lastSelectedOpponentIndex === "2"){
        return t("1_minute");
      }

      if(lastSelectedOpponentIndex === "1"){
        if (lastTimeLimit === "1") {
          return t("1_minute");
        } else if (lastTimeLimit === "30") {
          return t("30_minutes");
        } else if (lastTimeLimit === "5") {
          return t("5_minutes");
        } else if (lastTimeLimit === "3") {
          return t("3_minutes");
        } else {
          return "∞";
        }
      }
      else{
        return t("No Limit");
      }
    };

    const getTimeLimitColor = () => {
      const timeLimit = localStorage.getItem("lastTimeLimit");
      if (timeLimit === "1") {
        return 'linear-gradient(45deg, #ff9800, #f57c00)';
      } else if (timeLimit === "30") {
        return 'linear-gradient(45deg, #4caf50, #388e3c)';
      } else if (timeLimit === "5") {
        return 'linear-gradient(45deg, #2196f3, #1976d2)';
      } else if (timeLimit === "3") {
        return 'linear-gradient(45deg, #f44336, #d32f2f)';
      } else {
        return 'linear-gradient(45deg, #9c27b0, #7b1fa2)';
      }
    };

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 10,
        }}
      >
        <Chip
          icon={<AccessTimeIcon sx={{ color: 'white !important', fontSize: 14 }} />}
          label={getTimeLimitIcon()}
          size="small"
          sx={{
            background: getTimeLimitColor(),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.65rem',
            height: 24,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            '& .MuiChip-icon': {
              fontSize: 14,
              color: 'white',
            },
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      </Box>
    );
  };

  // User Type Badge Component
  const UserTypeBadge = () => {
    const userType = localStorage.getItem("lastOpponentHumanOrAI");
    let user = t(userType);
    let lastSelectedOpponentIndex = localStorage.getItem("lastSelectedOpponentIndex");

    if(lastSelectedOpponentIndex === "2"){
      user = t("Team");
    }
    
    const getUserTypeIcon = () => {
      if(lastSelectedOpponentIndex === "1"){
        return <PersonIcon sx={{ color: 'white !important', fontSize: 14 }} />;
      }
      if(lastSelectedOpponentIndex === "2"){
        return <GroupIcon sx={{ color: 'white !important', fontSize: 14 }} />;
      }

      if (userType === "Human") {
        return <PersonIcon sx={{ color: 'white !important', fontSize: 14 }} />;
      } else if (userType === "AI") {
        return <SmartToyIcon sx={{ color: 'white !important', fontSize: 14 }} />;
      } else {
        return <HelpOutlineIcon sx={{ color: 'white !important', fontSize: 14 }} />;
      }
    };

    const getUserTypeColor = () => {
      if (userType === "Human") {
        return 'linear-gradient(45deg, #4caf50, #388e3c)';
      } else if (userType === "AI") {
        return 'linear-gradient(45deg, #ff9800, #f57c00)';
      } else {
        return 'linear-gradient(45deg, #9c27b0, #7b1fa2)';
      }
    };

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
        }}
      >
        <Chip
          icon={getUserTypeIcon()}
          label={user || "Unknown"}
          size="small"
          sx={{
            background: getUserTypeColor(),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.65rem',
            height: 24,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            '& .MuiChip-icon': {
              fontSize: 14,
              color: 'white',
            },
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      </Box>
    );
  };

  const renderTime = ({ remainingTime }) => {
    if (remainingTime === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 96,
            width: 96,
            color: '#e53935',
            fontWeight: 600,
            fontSize: 16,
            fontFamily: 'sans-serif',
            textAlign: 'center',
            background: 'linear-gradient(45deg, #ff6b6b, #ff5252)',
            borderRadius: '50%',
            boxShadow: '0 4px 20px rgba(255, 107, 107, 0.4)',
          }}
        >
          {t("No opponent found")}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          position: 'relative',
          width: 96,
          height: 96,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, #4a707d, #2196F3)',
          color: 'white',
          boxShadow: '0 8px 25px rgba(74, 112, 125, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'sans-serif',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            width: '100%',
            textAlign: 'center',
            fontSize: 10,
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: 1,
            opacity: 0.9,
          }}
        >
          {t("Time Left")}
        </Box>

        <Box
          sx={{
            marginTop: "15px",
            fontSize: 28,
            fontWeight: 'bold',
            lineHeight: 1,
          }}
        >
          {remainingTime}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            width: '100%',
            textAlign: 'center',
            fontSize: 10,
            color: 'white',
            opacity: 0.9,
          }}
        >
          {t("seconds")}
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    const restartVideo = () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(console.error);
      }
    };

    const intervalId = setInterval(restartVideo, 20000);
    return () => clearInterval(intervalId);
  }, []);

  // Enhanced auto-start tour
  useEffect(() => {
    if (tourConditions.canShowTour && tourConditions.shouldShow && !tourConditions.hasTriggered) {
      const timer = setTimeout(() => {
        setAutoTourTriggered(true);
        startMainPageTour();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [tourConditions, startMainPageTour]);

  // Reset auto-trigger when conditions change
  useEffect(() => {
    let lastOpponentHumanOrAI = localStorage.getItem("lastOpponentHumanOrAI");
    let lastSelectedOpponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    if(lastOpponentHumanOrAI === "AI" && lastSelectedOpponentIndex === "2"){
      setSelectedOpponent("Human");
    }
    if (loading || showOpponentSelection || showLevelSelection) {
      setAutoTourTriggered(false);
    }
  }, [loading, showOpponentSelection, showLevelSelection]);

  useEffect(() => {
    let levelId = localStorage.getItem("lastSelectedIndexLevel");
    let opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    let timeLimit = localStorage.getItem("lastTimeLimit");
    setSelectedOpponent(localStorage.getItem("lastOpponentHumanOrAI"));
    
    if (!opponentIndex) {
      localStorage.setItem("lastSelectedOpponentIndex", 1);
    }
    if (!levelId) {
      localStorage.setItem("lastSelectedIndexLevel", 1);
    }
    if (!timeLimit) {
      localStorage.setItem("lastTimeLimit", 1);
    }
    if (setShowAppBar && typeof setShowAppBar === 'function') {
      setShowAppBar(true);
    }
  }, [setShowAppBar]);

  useEffect(() => {
    setButtonLabelsFromLocalStorage();
    const uncompletedGameFromStorage = localStorage.getItem("uncompleted_game");
    if (uncompletedGameFromStorage) {
      setUncompletedGame(JSON.parse(uncompletedGameFromStorage));
    }
  }, [setButtonLabelsFromLocalStorage]);

  useEffect(() => {
    if (uncompletedGameWS) {
      setUncompletedGame(uncompletedGameWS);
    }
  }, [uncompletedGameWS]);

  useEffect(() => {
    setLoading(false);
    const handlePopState = () => {};
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);

  useEffect(() => {
    document.body.style.overflow = loading ? "hidden" : "auto";
    updateBackground({ image: GENERAL.BACKGROUND });

    let lastOpponentHumanOrAI = localStorage.getItem("lastOpponentHumanOrAI");

    if(lastOpponentHumanOrAI === "undefined" || !lastOpponentHumanOrAI){
      localStorage.setItem("lastOpponentHumanOrAI","Human")
    } 
    
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [loading, updateBackground]);

  useEffect(() => {
    if (generalMessage) {
      playGame();
    }
  }, [generalMessage]);

  const cancelGame = () => {
    setLoading(false);
    const generalMessage = {
      ...generalMessageTemplate,
      messageType: "new_game_request_cancel",
    };
    sendMessage(generalMessage);
    if (setShowAppBar && typeof setShowAppBar === 'function') {
      setShowAppBar(true);
    }
  };

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);
  const handleBuyCoins = () => setIsDialogOpen(false);
  const handleWatchAd = () => setIsDialogOpen(false);

  const findLevelByTypeId = (levelTypeId) => {
    const storedLevels = JSON.parse(localStorage.getItem("levels"));
    return storedLevels?.find((level) => level.levelTypeId === +levelTypeId) || null;
  };

  const playGame = () => {
    setAnimatePlay(true);
    setTimeout(() => setAnimatePlay(false), 300);

    let levelId = localStorage.getItem("lastSelectedIndexLevel");
    let opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");

    if (!opponentIndex) {
      localStorage.setItem("lastSelectedOpponentIndex", 1);
    }
    if (!levelId) {
      localStorage.setItem("lastSelectedIndexLevel", 1);
    }

    levelId = localStorage.getItem("lastSelectedLevelId");
    opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    const storedOpponents = JSON.parse(localStorage.getItem("opponents"));

    const levelsObj = findLevelByTypeId(levelId);
    const opponentObj = storedOpponents?.[+opponentIndex];

    if (opponentObj?.gameType === "skill_building") {
      navigate("/learn", { state: { userData } });
      return;
    }

    if (opponentObj?.gameType === "blind") {
      navigate("/blind", { state: { userData } });
      return;
    }

    if (
      userData &&
      (Math.abs(levelsObj?.loseCoins) > userData.userStatistics.coins ||
        Math.abs(levelsObj?.drawCoins) > userData.userStatistics.coins)
    ) {
      setNeededCoins(
        Math.abs(levelsObj?.loseCoins) > Math.abs(levelsObj.drawCoins)
          ? Math.abs(levelsObj?.loseCoins)
          : Math.abs(levelsObj?.drawCoins)
      );
      setActualCoins(userData.userStatistics.coins);
      
      setTimeout(() => {
        handleOpenDialog();
      }, 100);
      return;
    }

    setLoading(true);
    if (setShowAppBar && typeof setShowAppBar === 'function') {
      setShowAppBar(false);
    }

    const generalMessageToSend = { ...generalMessageTemplate };
    generalMessageToSend.messageType = "new_game_request";
    generalMessageToSend.gameInfo.gameOpponentId = opponentIndex;
    generalMessageToSend.gameInfo.gameLevelId = levelId;

    generalMessageToSend.gameInfo.pointsWin = levelsObj?.win;
    generalMessageToSend.gameInfo.pointsLose = levelsObj?.lose;
    generalMessageToSend.gameInfo.pointsDraw = levelsObj?.draw;

    generalMessageToSend.gameInfo.coinsWin = levelsObj?.winCoins;
    generalMessageToSend.gameInfo.coinsDraw = levelsObj?.drawCoins;
    generalMessageToSend.gameInfo.coinsLose = levelsObj?.loseCoins;

    generalMessageToSend.gameInfo.gameTeamType = opponentObj?.gameType;
    generalMessageToSend.gameInfo.userType = "Human";
    generalMessageToSend.gameInfo.opponentType = localStorage.getItem("lastOpponentHumanOrAI");
    generalMessageToSend.gameInfo.secondsBasedOnGameTimeType = localStorage.getItem("lastTimeLimit");
    generalMessageToSend.gameInfo.aiLevel = localStorage.getItem("lastAILevel");

    sendMessage(generalMessageToSend);
  };

  const playWithFriends = () => {
    const levelIndex = localStorage.getItem("lastSelectedIndexLevel");
    const opponentIndex = localStorage.getItem("lastSelectedOpponentIndex");
    const levelsObj = levels[levelIndex];
    const opponentObj = opponents[opponentIndex];

    if (!levelsObj || !opponentObj) {
      setOpenDialog(true);
      return;
    }

    navigate("/team-builder", { state: { userData } });
  };

  const handleSelectOpponentClick = () => setShowOpponentSelection(true);
  const handleSelectLevelClick = () => setShowLevelSelection(true);
  const handleUncompletedGameClick = () => setGameInfoDialogOpen(true);

  const onOpponentSelected = () => {
    setShowOpponentSelection(false);
    setButtonLabelsFromLocalStorage();
    setSelectedOpponent(localStorage.getItem("lastOpponentHumanOrAI"));
  };

  const onLevelSelected = () => {
    setShowLevelSelection(false);
    setButtonLabelsFromLocalStorage();
  };

  const onContinue = () => {
    setGameInfoDialogOpen(false);
    const uncompletedGameData = JSON.parse(localStorage.getItem("uncompleted_game"));
    if (uncompletedGameData) {
      const generalMessageToSend = { ...generalMessageTemplate };
      generalMessageToSend.messageType = "continue_game";
      generalMessageToSend.gameInfo = uncompletedGameData;

      sendMessage(generalMessageToSend);
    }
  };

  // Enhanced manual tour handler
  const handleManualTour = useCallback(() => {
    setAutoTourTriggered(true);
    forceShowMainPageTour(); // Use force show to override session closure
  }, [forceShowMainPageTour]);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  };

  // Enhanced Game Stats Component
  const GameStatsCard = () => (
    <Slide direction="down" in={showGameStats} mountOnEnter unmountOnExit>
      <Card
        sx={{
          position: 'fixed',
          top: 100,
          left: 20,
          right: 20,
          maxWidth: 400,
          mx: 'auto',
          background: 'linear-gradient(135deg, rgba(74, 112, 125, 0.95), rgba(33, 150, 243, 0.95))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          zIndex: 1000,
        }}
      >
        <CardContent sx={{ color: 'white' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              {t("Game Statistics")}
            </Typography>
            <IconButton size="small" onClick={() => setShowGameStats(false)}>
              <Close sx={{ color: 'white' }} />
            </IconButton>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box textAlign="center">
                <EmojiEvents sx={{ fontSize: 30, color: '#FFD700' }} />
                <Typography variant="h6" fontWeight="bold">
                  {userData?.userStatistics?.wins || 0}
                </Typography>
                <Typography variant="caption">{t("Wins")}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Star sx={{ fontSize: 30, color: '#FFA726' }} />
                <Typography variant="h6" fontWeight="bold">
                  {userData?.userStatistics?.rating || 0}
                </Typography>
                <Typography variant="caption">{t("Rating")}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Star sx={{ fontSize: 30, color: '#FFA726' }} />
                <Typography variant="h6" fontWeight="bold">
                  {userData?.userStatistics?.coins || 0}
                </Typography>
                <Typography variant="caption">{t("Coins")}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="center">
                <Speed sx={{ fontSize: 30, color: '#42A5F5' }} />
                <Typography variant="h6" fontWeight="bold">
                  {userData?.userStatistics?.gamesPlayed || 0}
                </Typography>
                <Typography variant="caption">{t("Games")}</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Slide>
  );

  return (
    <Box
      data-tour="main-container" // Tour selector for main container
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: `url(${GENERAL.BACKGROUND})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Centered Background Video */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '100%', md: '30%' },
          height: { xs: '100%', md: '100%' },
          zIndex: 0,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.3), rgba(74, 112, 125, 0.2))',
            zIndex: 1,
          }
        }}
      >
        <VideoPlayer
          isSelected={true}
          ref={videoRef}
          videoSrc={"https://res.cloudinary.com/dnlgtek2a/video/upload/v1743368069/video/mhmthyliy6zoj7qxojac.mp4"}
          autoPlay={false}
          loop={false}
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '12px',
          }}
        />
      </Box>

      {/* Video Controls */}
      <Fab
        size="small"
        onClick={toggleVideo}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
          background: 'rgba(74, 112, 125, 0.8)',
          '&:hover': {
            background: 'rgba(74, 112, 125, 1)',
          },
        }}
      >
        {videoPlaying ? <VisibilityOff /> : <Visibility />}
      </Fab>

      {/* Enhanced Tour Control with session-based behavior */}
      {!loading && !showOpponentSelection && !showLevelSelection && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 20, sm: 30 },
            left: { xs: 20, sm: 30 },
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Tooltip title={t("Show Game Tutorial")} placement="right">
            <Fab
              size={isMobile ? "small" : "medium"}
              onClick={handleManualTour}
              sx={{
                background: 'linear-gradient(45deg, #4a707d, #2196F3)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(74, 112, 125, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 6px 25px rgba(74, 112, 125, 0.6)',
                },
                // Add pulse animation for new users - fix shouldShowTour usage
                animation: !hasAutoStarted && (typeof shouldShowTour === 'function' ? shouldShowTour() : false) ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 4px 20px rgba(74, 112, 125, 0.4)',
                  },
                  '50%': {
                    boxShadow: '0 4px 30px rgba(74, 112, 125, 0.8)',
                    transform: 'scale(1.05)',
                  },
                  '100%': {
                    boxShadow: '0 4px 20px rgba(74, 112, 125, 0.4)',
                  },
                },
              }}
            >
              <TourIcon />
            </Fab>
          </Tooltip>
        </Box>
      )}

      {/* Game Stats Card */}
      <GameStatsCard />

      {/* Enhanced Dialogs */}
      <NotEnoughCoinsDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onBuyCoins={handleBuyCoins}
        onWatchAd={handleWatchAd}
        neededCoins={neededCoins}
        actualCoins={actualCoins}
      />

      <GameInfoDialog
        open={gameInfoDialogOpen}
        onClose={() => setGameInfoDialogOpen(false)}
        gameInfo={uncompletedGame}
        operations={true}
        onContinue={onContinue}
      />

      {/* Enhanced Carousel Dialogs */}
      <Dialog
        open={showOpponentSelection}
        onClose={() => setShowOpponentSelection(false)}
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(0,0,0,0.9), rgba(74, 112, 125, 0.9))',
            backdropFilter: 'blur(20px)',
            border: '5px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <OpponentSelectionCarousel
            onOpponentSelected={onOpponentSelected}
            onFriendSelected={playWithFriends}
            scaleConfig={{ selected: isMobile ? 1 : 1 }}
            setShowAppBar={setShowAppBar}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={showLevelSelection}
        onClose={() => setShowLevelSelection(false)}
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(0,0,0,0.9), rgba(74, 112, 125, 0.9))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <LevelSelectionCarousel
            onLevelSelected={onLevelSelected}
            scaleConfig={{ selected: isMobile ? 1 : 1 }}
            setShowAppBar={setShowAppBar}
          />
        </DialogContent>
      </Dialog>

      {/* Enhanced Main Interface with tour selectors */}
      {!showOpponentSelection && !showLevelSelection && !loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 2, sm: 4 },
            overflow: 'hidden',
            marginBottom: "-120px"
          }}
        >
          {/* Action Cards - Centered */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mb: 4,
              width: '100%',
              maxWidth: 400,
            }}
          >
            {/* Enhanced Opponent Selection Card with tour integration */}
            <Box data-tour="select-opponent" sx={{ position: 'relative'}} >
              {/* Time Limit Badge in top-left corner */}
              <TimeLimitBadge />
              
              {/* User Type Badge in top-right corner */}
              <UserTypeBadge />
              
              <Card
                sx={{
                  background: { 
                    xs: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
                    md: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))'
                  },
                  backdropFilter: 'blur(2px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  height: 180,
                  // Add subtle highlight when tour is available - fix shouldShowTour usage
                  ...(typeof shouldShowTour === 'function' && shouldShowTour() && !hasAutoStarted && {
                    border: '2px solid rgba(33, 150, 243, 0.3)',
                    boxShadow: '0 0 20px rgba(33, 150, 243, 0.2)',
                  }),
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)',
                  },
                }}
                onClick={handleSelectOpponentClick}
              >
                <CardContent sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box mb={2}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mx: 'auto',
                        background: 'linear-gradient(45deg, #4a707d, #2196F3)',
                        boxShadow: '0 4px 20px rgba(74, 112, 125, 0.4)',
                      }}
                    >
                      {selectedOpponent === "Human" ? (
                        <PersonIcon sx={{ fontSize: 30, color: 'white' }} />
                      ) : selectedOpponent === "AI" ? (
                        <SmartToyIcon sx={{ fontSize: 30, color: 'white' }} />
                      ) : (
                        <HelpOutlineIcon sx={{ fontSize: 30, color: 'white' }} />
                      )}
                    </Avatar>
                  </Box>
                  
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: 'white',
                      textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                      mb: 1,
                      fontSize: '1.1rem'
                    }}
                  >
                    {t("Select Your Game Type")}
                  </Typography>
                  
                  <Chip
                    label={buttonSelectOpponent}
                    sx={{
                      background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.8rem',
                      boxShadow: '0 2px 10px rgba(255, 107, 107, 0.3)',
                    }}
                  />
                </CardContent>
              </Card>
            </Box>

            {/* Enhanced Level Selection Card with tour integration */}
            {localStorage.getItem("lastSelectedOpponentIndex") <= 2 && (
              <Box data-tour="select-level" >
                <Card
                  sx={{
                    background: { 
                      xs: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
                      md: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))'
                    },
                    backdropFilter: 'blur(2px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    minHeight: 220, // Increased height to accommodate outcome details
                    // Add subtle highlight when tour is available - fix shouldShowTour usage
                    ...(typeof shouldShowTour === 'function' && shouldShowTour() && !hasAutoStarted && {
                      border: '2px solid rgba(33, 150, 243, 0.3)',
                      boxShadow: '0 0 20px rgba(33, 150, 243, 0.2)',
                    }),
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)',
                    },
                  }}
                  onClick={handleSelectLevelClick}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    {/* Header Section */}
                    <Box>
                      <Box mb={2}>
                        <Avatar
                          sx={{
                            width: 50,
                            height: 50,
                            mx: 'auto',
                            background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
                            boxShadow: '0 4px 20px rgba(255, 107, 107, 0.4)',
                          }}
                        >
                          <HowToVoteIcon sx={{ fontSize: 24, color: 'white' }} />
                        </Avatar>
                      </Box>
                      
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          color: 'white',
                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                          mb: 1,
                          fontSize: '1rem'
                        }}
                      >
                        {t("Select Outcome Rewards")}
                      </Typography>
                      
                      <Chip
                        label={buttonSelectLevel}
                        sx={{
                          background: 'linear-gradient(45deg, #4a707d, #2196F3)',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          boxShadow: '0 2px 10px rgba(74, 112, 125, 0.3)',
                          mb: 2,
                        }}
                      />
                    </Box>

                    {/* Outcomes Section */}
                    {(() => {
                      const levelData = getCurrentLevelData();
                      return levelData && (
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', gap: 0.8, justifyContent: 'space-between' }}>
                            {/* Win */}
                            <OutcomeItem
                              icon={<EmojiEventsIcon sx={{ fontSize: 14 }} />}
                              coins={levelData.winCoins}
                              color="#4CAF50"
                            />
                            
                            {/* Draw */}
                            <OutcomeItem
                              icon={<BalanceIcon sx={{ fontSize: 14 }} />}
                              coins={levelData.drawCoins}
                              color="#FF9800"
                            />
                            
                            {/* Defeat */}
                            <OutcomeItem
                              icon={<CloseIcon sx={{ fontSize: 14 }} />}
                              coins={levelData.loseCoins}
                              color="#f44336"
                            />
                          </Box>
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>

          {/* Enhanced Play Button with tour integration */}
          <Box data-tour="play-button" sx={{ mb: 4, marginTop: "-18px" }}>
            <Zoom in timeout={500}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: animatePlay ? 'scale(0.95)' : 'scale(1)',
                  boxShadow: '0 10px 30px rgba(76, 175, 80, 0.4)',
                  // Add subtle highlight when tour is available - fix shouldShowTour usage
                  ...(typeof shouldShowTour === 'function' && shouldShowTour() && !hasAutoStarted && {
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 10px 35px rgba(76, 175, 80, 0.6)',
                  }),
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 15px 40px rgba(76, 175, 80, 0.6)',
                  },
                }}
                onClick={playGame}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                    <PlayArrowIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: 'white' }} />
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                        fontSize: { xs: '1.5rem', sm: '2rem' }
                      }}
                    >
                      {t("Play!")}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Box>

          {/* Quick Actions - Continue Game */}
          {uncompletedGame && (
            <Slide direction="up" in mountOnEnter unmountOnExit>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 152, 0, 0.9))',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
                onClick={handleUncompletedGameClick}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Badge badgeContent="!" color="error">
                      <SportsEsports sx={{ fontSize: 30, color: 'white' }} />
                    </Badge>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                        {t("Continue Game")}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {t("You have an unfinished game")}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Slide>
          )}
        </Box>
      )}

      {/* Enhanced Loading Screen */}
      {loading && (
        <Backdrop open={loading} sx={{ zIndex: 1500 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: 4,
            }}
          >
            {/* Loading Header */}
            <Card
              sx={{
                mb: 4,
                background: 'linear-gradient(135deg, rgba(0,0,0,0.8), rgba(74, 112, 125, 0.8))',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                minWidth: { xs: 280, sm: 400 },
              }}
            >
              <CardContent>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  fontWeight="bold"
                  sx={{
                    color: '#FFD700',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                    mb: 1,
                  }}
                >
                  {t("Finding Opponent")}
                </Typography>
                <LinearProgress
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                    },
                  }}
                />
              </CardContent>
            </Card>

            {/* Loading Animation */}
            <Box mb={4}>
              <LottieAnimation
                path={"https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/search.json"}
                width={isMobile ? "200px" : "300px"}
                height={isMobile ? "200px" : "300px"}
                autoplay
                loop
              />
            </Box>

            {/* Enhanced Timer */}
            <Box mb={4}>
              <CountdownCircleTimer
                isPlaying
                duration={60}
                colors={["#4a707d", "#F7B801", "#A30000", "#A30000"]}
                colorsTime={[45, 30, 15, 0]}
                onComplete={() => ({ shouldRepeat: false, delay: 1 })}
                size={120}
                strokeWidth={8}
              >
                {renderTime}
              </CountdownCircleTimer>
            </Box>

            {/* Cancel Button */}
            <Zoom in timeout={1000}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #f44336, #d32f2f)',
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 10px 30px rgba(244, 67, 54, 0.4)',
                  },
                }}
                onClick={cancelGame}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <CancelIcon sx={{ fontSize: 24, color: 'white' }} />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: 'white' }}
                    >
                      {t("Stop_Waiting")}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Box>
        </Backdrop>
      )}

      {/* Enhanced Error Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(74, 112, 125, 0.95), rgba(33, 150, 243, 0.95))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: 'white',
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          }}
        >
          {t("Selection Required")}
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', color: 'white' }}>
          <Box mb={3}>
            <MovingText
              type="typewriter"
              dataText={[t("Select Your Game Type"), t("Select Outcome Rewards")]}
            />
          </Box>

          <Box sx={{ maxWidth: 300, mx: 'auto' }}>
            <LottieAnimation
              path={"https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418439/Lottie/needToSelect.json"}
              width="100%"
              autoplay
              loop
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #4CAF50, #45a049)',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.6)',
              },
            }}
          >
            {t("OK")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MainPage;